import { twMerge } from '@/lib/classname/twMerge'
import { twConfig } from '@/lib/tailwind/twConfig'
import { cva, type VariantProps } from 'class-variance-authority'
import type React from 'react'
import { type ReactNode } from 'react'
import sanitizeHtml from 'sanitize-html'
import { SANITIZE_HTML_OPTIONS } from '@/lib/utils/text-utils'

export const titleElements = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const
export const titleSizes = ['sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl'] as const
export type TitleSize = (typeof titleSizes)[number]
export const titleLineHeights = Object.keys(twConfig.theme.lineHeight)

export type TitleElement = (typeof titleElements)[number]

export const titleCva = cva('', {
  variants: {
    size: {
      sm: 'text-sm',
      md: 'text-md font-bold',
      lg: 'text-lg',
      xl: 'text-lg desktop:text-xl',
      '2xl': 'text-xl desktop:text-2xl',
      '3xl': 'text-2xl desktop:text-3xl',
      '4xl': 'text-3xl desktop:text-4xl',
      '5xl': 'text-4xl desktop:text-5xl',
      '6xl': 'text-5xl desktop:text-6xl',
    } satisfies Record<TitleSize, string>,
    lineHeight: {
      compact: 'leading-compact',
      normal: 'leading-normal',
      light: 'leading-light',
      lighter: 'leading-lighter',
      lightest: 'leading-lightest',
    } satisfies Record<(typeof titleLineHeights)[number], string>,
  },
  defaultVariants: {
    size: 'xl',
    lineHeight: 'normal',
  },
})

type TitleVariantProps = VariantProps<typeof titleCva>

export type TitleProps<ElementType extends TitleElement = TitleElement> = TitleVariantProps & {
  as?: ElementType
  className?: string
} & (
    | { childrenAsInnerHTML: true; children: string }
    | { childrenAsInnerHTML?: never; children: ReactNode }
  )

export function Title<ElementType extends TitleElement = TitleElement>({
  as,
  size,
  lineHeight,
  children,
  className,
  childrenAsInnerHTML,
  ...props
}: TitleProps<ElementType> & React.ComponentPropsWithoutRef<ElementType>): React.ReactElement {
  const Component = as || 'h1'

  return (
    <Component
      className={twMerge(className, titleCva({ size, lineHeight }))}
      data-testid="title"
      {...props}
      {...(childrenAsInnerHTML
        ? {
            dangerouslySetInnerHTML: {
              __html: sanitizeHtml(children as string, SANITIZE_HTML_OPTIONS),
            },
          }
        : { children })}
    />
  )
}
